import { useState, useCallback } from 'react';

export type FormValue = { [key: string]: any; };
export type FormErrors = { [key: string]: { message: string }[] };
export interface RulesItem {
  required?: boolean; 
  message?: string;
  pattern?: RegExp | ((value: any) => boolean);
  max?: number | string;
  min?: number | string;
}
export type ValidatorRules = Record<string, RulesItem[]>;

export interface UseFormRes {
  getFieldsValue: (nameList?: string[]) => FormValue | any[];
  getFieldValue: (key: string) => any;
  setFieldsValue: (params: Record<string, any>) => void;
  getFieldsError: () => FormErrors,
  validateFields: (namePath?: string[]) => Promise<FormValue | FormErrors>;
  resetFields: () => void;
  setFieldsValidatorRules: (params: Record<string, any>) => void;
  validatorRules: ValidatorRules;
  removeFieldErrors: (key: string) => void;
  setFieldsError: (params?: Record<string, any>) => void;
}

const useForm = (): UseFormRes => {

  const [values, setValues] = useState<FormValue>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const [validatorRules, setValidatorRules] = useState<ValidatorRules>({});

  const getFieldsError = () => {
    return errors;
  }

  const removeFieldErrors = (key: string) => {
    const newError = { ...errors };
    if (key in newError) {
      if (newError[key]) {
        delete newError[key];
      }
    }
    setErrors(newError)
  }

  const setFieldsError = (params?: Record<string, any>) => {
    setErrors(oldError => ({ ...oldError, ...params }));
  }

  const getFieldsValue = (nameList?: string[]) => {
    if (!nameList) {
      return values;
    }
    const valueList: any[] = [];
    for (const item of nameList) {
      if (values[item]) {
        valueList.push(values[item]);
      }
    }

    return valueList;
  }

  const getFieldValue = (key: string) => {
    return values[key];
  }

  const setFieldsValue = useCallback((params: Record<string, any>) => {
    setValues(oldValues => ({ ...oldValues, ...params }));
  }, [])

  const setFieldsValidatorRules = useCallback((params: Record<string, any>) => {
    setValidatorRules(oldRules => ({ ...oldRules, ...params }));
  }, [])

  const validateFields = useCallback( async (namePath: string[] = []) => {

    return new Promise<FormValue | FormErrors>((resolve, reject) => { 
      const newErrors: FormErrors = {};

      for (const fieldName in validatorRules) {
        if ((namePath?.length > 0 && namePath.includes(fieldName)) || namePath.length === 0) {
          const fieldRules = validatorRules[fieldName];
          const fieldValue = values[fieldName];
          for (const rule of fieldRules) {
            if (rule.required && !fieldValue) {
              newErrors[fieldName] = [{ message: rule.message || 'Required' }];
              break;
            }
            if (rule.pattern) {
              if (!rule.required && !fieldValue) {
                break;
              }

              if (rule.pattern instanceof RegExp) {
                if (!rule.pattern?.test(fieldValue)) {
                  newErrors[fieldName] = [{ message: rule.message || 'Error' }];
                  break;
                }
              }
              
              if (typeof rule.pattern === 'function') {
                // @ts-ignore
                if (rule.pattern?.(fieldValue)) {
                  newErrors[fieldName] = [{ message: rule.message || 'Error' }];
                  break;
                }
              }
            }
          }
        }    
      }
      if (Object.keys(newErrors).length === 0) {
        resolve(values as FormValue | FormErrors); 
      } else {
        setErrors(newErrors);
        reject(newErrors as FormValue | FormErrors); 
      }
    })
  }, [values, validatorRules]);

  const resetFields = () => {
    setValues({});
    setErrors({})
  }

  return {
    getFieldsValue,
    getFieldValue,
    setFieldsValue,
    getFieldsError,
    validateFields,
    resetFields,
    setFieldsValidatorRules,
    validatorRules,
    removeFieldErrors,
    setFieldsError,
  }
}

export default useForm;

